import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import SpecialsContent from './SpecialsContent';
import { useOrderModal } from '../contexts/OrderModalContext';

const Specials = () => {
  const [specialsData, setSpecialsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { day } = useParams(); // Get day from URL params instead of search params
  const location = useLocation();
  const { openModal } = useOrderModal();

  // ✅ Use params instead of searchParams
  const requestedDay = day;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchSpecials();
  }, [requestedDay, location.pathname]); // Watch pathname instead of search

  const fetchSpecials = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Determine which endpoint to call
      let endpoint = '/api/specials/today'; // Default to today
      
      if (requestedDay) {
        // Validate the day parameter
        const validDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        if (validDays.includes(requestedDay.toLowerCase())) {
          endpoint = `/api/specials/day/${requestedDay}`;
        } else {
          // Invalid day, redirect to today's special
          navigate('/daily-specials', { replace: true });
          return;
        }
      }

      const response = await fetch(endpoint);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to fetch specials: ${response.status}`);
      }

      if (data.success) {
        setSpecialsData(data.data);
      } else {
        throw new Error(data.message || 'Failed to load specials');
      }
    } catch (err) {
      console.error('Error fetching specials:', err);
      setError(err.message || 'Unable to load today\'s special. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrderClick = (special, dayOfWeek) => {
    // Use the same order modal system as the rest of the site
    
    
    // Open the order modal - no preselected location for specials page
    openModal(null);
  };

  const handleRetry = () => {
    fetchSpecials();
  };

  // Helper function to get the current day name for comparison
  const getCurrentDayName = () => {
    if (!requestedDay) return null;
    return requestedDay.charAt(0).toUpperCase() + requestedDay.slice(1).toLowerCase();
  };

  return (
    <section className="theme-light section m-0 p-0" data-theme="light">
      <div className='is-flex is-flex-direction-column is-justify-content-start' style={{ minHeight: '100vh' }}>
        
        {/* Navigation - overlay style for hero layout */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100 }}>
          <Navigation 
            colorMode="colorWhite"
          />
        </div>
        
        {/* Hero Special Content */}
        <SpecialsContent 
          special={specialsData}
          dayOfWeek={specialsData?.dayOfWeek}
          isLoading={isLoading}
          error={error}
          onOrderClick={handleOrderClick}
        />
        
        {/* Additional content section (optional) */}
        {specialsData && !isLoading && (
          <section className="section py-6" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="container">
              <div className="columns is-centered">
                <div className="column is-10-tablet is-8-desktop has-text-centered">
                    {/* Weekly specials preview */}
                    <div className="mt-6">
                      <h3 className="title is-size-4 mb-4">Daily Specials</h3>
                      <p className="mx-6 mb-4">Seven days, seven reasons to thank Kinder’s Pitboss, Dan Kinder. The man who’s been tending the fire and perfecting the flavor for decades. Each special is smoked low, grilled hot, and always worth the trip.</p>
                      
                      <div className="buttons is-centered">
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                          <button 
                            key={day}
                            onClick={() => navigate(`/daily-specials/${day.toLowerCase()}`)}
                            className={`button widthButton7 ${getCurrentDayName() === day ? 'is-primary' : 'is-light'}`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                      
                    </div>
                    
                  </div>
                </div>
              </div>
            
          </section>
        )}
        
        {/* Error state with retry option */}
        {error && !isLoading && (
          <section className="section py-6">
            <div className="container">
              <div className="columns is-centered">
                <div className="column is-6 has-text-centered">
                  <button 
                    className="button is-primary is-outlined"
                    onClick={handleRetry}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}
        
        <Footer />
        
      </div>
    </section>
  );
};

export default Specials;